import type { ReactNode } from "react";
import {
	DisplayFieldFeedback,
	type DisplayFieldFeedback as DisplayFieldFeedbackType,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { FeedbackIndicator } from "@/components/Atoms/FeedbackIndicator";
import { Label } from "@/components/base/input/label";
import { cx } from "@/utils/cx";

export interface LabelWithFeedbackProps {
	label?: string;
	isRequired?: boolean;
	/** When true, the required asterisk uses the filled (brand) color. */
	isFilled?: boolean;
	tooltip?: string;
	feedback?: DisplayFieldFeedbackType;
	/** Extra props forwarded to the label element (e.g. htmlFor, id). */
	labelProps?: Omit<React.ComponentProps<typeof Label>, "children">;
	className?: string;
	children?: ReactNode;
}

/**
 * Figma Input label wrapper: label (+ required *) on the left, FeedbackIndicator on the right.
 */
export function LabelWithFeedback({
	label,
	isRequired,
	isFilled = false,
	tooltip,
	feedback = DisplayFieldFeedback.Idle,
	labelProps,
	className,
	children,
}: LabelWithFeedbackProps) {
	const showFeedback =
		feedback === DisplayFieldFeedback.Saving ||
		feedback === DisplayFieldFeedback.Saved ||
		feedback === DisplayFieldFeedback.End;

	if (!label && !showFeedback && !children) return null;

	return (
		<div
			className={cx(
				"flex w-full items-center justify-between gap-2",
				className,
			)}
			data-label-row="true"
		>
			{label ? (
				<Label
					isRequired={isRequired}
					tooltip={tooltip}
					{...labelProps}
					isFilled={isFilled}
				>
					{label}
				</Label>
			) : (
				<span />
			)}
			{children}
			<FeedbackIndicator feedback={feedback} />
		</div>
	);
}
